/**
 * CatalogoSelect
 * ─────────────────────────────────────────────────────────────────
 * Combobox con buscador contra un catálogo (cat_profesion o
 * cat_actividad_economica). El operador teclea, ve sugerencias en
 * vivo, y selecciona una. También permite valores libres si lo
 * que escribe no coincide con nada del catálogo.
 *
 * Búsqueda: contra `nombre` (ILIKE) o contra el array `sinonimos`.
 * Ranking simple: matches que empiezan por el texto primero, luego
 * matches en cualquier posición. Limita a 30 resultados.
 *
 * Accesibilidad: arrow keys + Enter + Esc. Aria roles combobox/listbox.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CaretDown, MagnifyingGlass, X } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type CatalogoRow = {
  codigo: string
  nombre: string
  grupo: string | null
  sinonimos: string[] | null
}

type Props = {
  value: string
  onChange: (nombre: string, codigo?: string) => void
  placeholder?: string
  required?: boolean
  preload?: boolean
  /** Tabla del catálogo: 'profesion' o 'actividad'. Default 'profesion'. */
  catalogo?: 'profesion' | 'actividad'
}

function CatalogoSelect({ value, onChange, placeholder = 'Buscá…', required, preload = true, catalogo = 'profesion' }: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(value)
  const [highlight, setHighlight] = useState(0)
  const [rows, setRows] = useState<CatalogoRow[]>([])
  const [loaded, setLoaded] = useState(false)

  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  // Mantener query sincronizado con value externo (cuando llega del OCR)
  useEffect(() => { setQuery(value) }, [value])

  // Cargar catálogo al montar (rows pequeños, ~30kb — fine para client-side)
  useEffect(() => {
    if (!preload || loaded) return
    void (async () => {
      const tabla = catalogo === 'actividad' ? 'cat_actividad_economica' : 'cat_profesion'
      const grupoCol = catalogo === 'actividad' ? 'sector' : 'grupo_primario'
      const { data } = await supabase
        .from(tabla)
        .select(`codigo, nombre, ${grupoCol}, sinonimos`)
        .eq('activo', true)
        .order('nombre')
      if (data) {
        setRows(
          (data as Array<Record<string, unknown>>).map((r) => ({
            codigo: String(r.codigo),
            nombre: String(r.nombre),
            grupo: (r[grupoCol] as string) ?? null,
            sinonimos: (r.sinonimos as string[]) ?? null,
          })),
        )
      }
      setLoaded(true)
    })()
  }, [preload, loaded, catalogo])

  // Cerrar al click fuera
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Filtro en memoria. Normalizamos quitando tildes y case.
  const filtered = useMemo<CatalogoRow[]>(() => {
    const q = norm(query)
    if (!q) return rows.slice(0, 30)
    const exact: CatalogoRow[] = []
    const starts: CatalogoRow[] = []
    const contains: CatalogoRow[] = []
    for (const p of rows) {
      const n = norm(p.nombre)
      const sinHit = (p.sinonimos ?? []).some(s => norm(s).includes(q))
      if (n === q) exact.push(p)
      else if (n.startsWith(q)) starts.push(p)
      else if (n.includes(q) || sinHit) contains.push(p)
    }
    return [...exact, ...starts, ...contains].slice(0, 30)
  }, [query, rows])

  const commitText = useCallback((text: string, codigo?: string) => {
    onChange(text.toUpperCase(), codigo)
    setQuery(text.toUpperCase())
    setOpen(false)
    setHighlight(0)
  }, [onChange])

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setHighlight(h => Math.min(h + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(h => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const hit = filtered[highlight]
      if (open && hit) commitText(hit.nombre, hit.codigo)
      else commitText(query)
    } else if (e.key === 'Escape') {
      setOpen(false)
    } else if (e.key === 'Tab' && open) {
      const hit = filtered[highlight]
      if (hit) commitText(hit.nombre, hit.codigo)
    }
  }

  // Scroll auto-follow del highlight
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelectorAll('li')[highlight] as HTMLLIElement | undefined
    el?.scrollIntoView({ block: 'nearest' })
  }, [highlight, open])

  const clear = () => {
    setQuery('')
    onChange('')
    inputRef.current?.focus()
  }

  return (
    <div className="profesion-select" ref={wrapRef}>
      <div className="profesion-select-input">
        <MagnifyingGlass size={14} weight="bold" className="ps-icon" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={placeholder}
          required={required}
          onChange={(e) => {
            setQuery(e.target.value)
            setHighlight(0)
            setOpen(true)
            onChange(e.target.value.toUpperCase())
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          spellCheck={false}
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {query && (
          <button type="button" className="ps-clear" onClick={clear} title="Limpiar">
            <X size={14} />
          </button>
        )}
        <CaretDown size={14} weight="bold" className="ps-caret" onClick={() => { setOpen(o => !o); inputRef.current?.focus() }} />
      </div>

      {open && (
        <div className="profesion-select-pop">
          {!loaded ? (
            <div className="ps-empty">Cargando catálogo…</div>
          ) : filtered.length === 0 ? (
            <div className="ps-empty">
              Sin coincidencias. Apretá <kbd>Enter</kbd> para guardar “{query.toUpperCase()}” tal cual.
            </div>
          ) : (
            <ul ref={listRef} role="listbox">
              {filtered.map((p, i) => (
                <li
                  key={p.codigo}
                  role="option"
                  aria-selected={i === highlight}
                  className={i === highlight ? 'hi' : ''}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => { e.preventDefault(); commitText(p.nombre, p.codigo) }}
                >
                  <div className="ps-main">{p.nombre}</div>
                  <div className="ps-meta">
                    <span className="ps-code">{p.codigo}</span>
                    {p.sinonimos && p.sinonimos.length > 0 && (
                      <span className="ps-syn">· {p.sinonimos.slice(0, 3).join(', ').toLowerCase()}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function norm(s: string): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

// ─────────────────────────────────────────────────────────────────
// Wrappers públicos
// ─────────────────────────────────────────────────────────────────

export function ProfesionSelect(props: Omit<Props, 'catalogo'>) {
  return (
    <CatalogoSelect
      {...props}
      catalogo="profesion"
      placeholder={props.placeholder ?? 'Buscá: doctor, mecánico, ama de casa…'}
    />
  )
}

export function ActividadEconomicaSelect(props: Omit<Props, 'catalogo'>) {
  return (
    <CatalogoSelect
      {...props}
      catalogo="actividad"
      placeholder={props.placeholder ?? 'Buscá: tienda, café, salón de belleza…'}
    />
  )
}
